import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";

import { getErpAuthState, isErpApiSuccess } from "../erpClient";
import { hrmsApi } from "../utils/hrmsApi";
import { hrmsErrorToast } from "../utils/hrmsErrorToast";

import "./EssHolidaysLeavesTab.css";

const CALENDAR_KEY_TO_CLASS = {
  Danger: "danger",
  Success: "success",
  Primary: "primary",
  Warning: "warning",
};

const CALENDAR_CATEGORY_LABELS = {
  Danger: "Half day",
  Success: "Overtime",
  Primary: "Holiday",
  Warning: "Leave",
};

const ESS_LEGEND_ITEMS = [
  { calendar: "Danger", background: "var(--bs-danger)" },
  { calendar: "Success", background: "rgb(var(--bs-success-rgb))" },
  { calendar: "Primary", background: "#635bff" },
  { calendar: "Warning", background: "var(--bs-orange, #fd7e14)" },
].map(({ calendar, background }) => ({
  key: calendar,
  label: CALENDAR_CATEGORY_LABELS[calendar],
  background,
}));

function resolveTenantId() {
  const u = getErpAuthState().user || {};
  const n = Number(u.tenant_id ?? u.tenantId);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function withTenantId(payload, tenantId) {
  if (tenantId) return { ...payload, tenant_id: tenantId };
  return payload;
}

function mapHolidayListToFcEvents(holidays) {
  return holidays.map((h) => {
    const id = h?.id != null ? String(h.id) : "";
    const dateStr = String(h?.holiday_date ?? "").slice(0, 10);
    return {
      id: id
        ? `hrms-holiday-${id}`
        : `hrms-holiday-row-${dateStr}-${String(h?.name ?? "")}`,
      title: String(h?.name ?? "Holiday").trim() || "Holiday",
      start: dateStr,
      allDay: true,
      extendedProps: {
        calendar: "Primary",
        kind: "holiday",
        source: "hrms",
      },
    };
  });
}

function isHrmsHolidayEventId(eventId) {
  return String(eventId ?? "").startsWith("hrms-holiday-");
}

/** One accent for bar + text + FC dot (avoid theme/link overrides on dot-layout events). */
function essReadRootVar(name, fallback = "") {
  if (typeof document === "undefined") return fallback.trim();
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
  return raw.trim() || fallback.trim();
}

function essAccentHexForFcBg(fcBg) {
  switch (fcBg) {
    case "danger":
      return essReadRootVar("--bs-danger", "#ff6692");
    case "success":
      return `rgb(${essReadRootVar("--bs-success-rgb", "54, 199, 108")})`;
    case "warning":
      return essReadRootVar("--bs-orange", "#fd7e14");
    case "primary":
      /* Theme --bs-primary is maroon — never use it for the stripe; match pill + label */
      return "#635bff";
    default:
      return "";
  }
}

function essResolveEventAnchor(el) {
  if (!(el instanceof HTMLElement)) return null;
  return el.matches("a.fc-event") ? el : el.closest("a.fc-event");
}

function essPaintUnifiedEventAccent(rawEl, fcBg) {
  const el = essResolveEventAnchor(rawEl);
  if (!el) return;
  const accent = essAccentHexForFcBg(fcBg);
  if (!accent) return;
  const imp = "important";

  el.style.setProperty("--ess-event-accent", accent);
  el.style.setProperty("color", accent, imp);

  /* Theme .fc-bg-primary uses border-color: var(--bs-primary) (maroon). Force one accent for the 4px stripe. */
  el.style.setProperty("border-width", "0 0 0 4px", imp);
  el.style.setProperty("border-style", "solid", imp);
  el.style.setProperty("border-top-color", "transparent", imp);
  el.style.setProperty("border-right-color", "transparent", imp);
  el.style.setProperty("border-bottom-color", "transparent", imp);
  el.style.setProperty("border-left-color", accent, imp);

  el.querySelectorAll(".fc-event-main").forEach((n) =>
    n.style.setProperty("color", accent, imp),
  );
  el.querySelectorAll(".fc-event-time, .fc-event-title").forEach((n) =>
    n.style.setProperty("color", accent, imp),
  );
  el.querySelectorAll(".fc-daygrid-event-dot").forEach((n) =>
    n.style.setProperty("border-color", accent, imp),
  );
}

/** Match `public/assets/js/apps/calendar-init.js` — month padded 01–12 */
function paddedMonth(date) {
  const m = date.getMonth() + 1;
  return m < 10 ? `0${m}` : `${m}`;
}

export function EssHolidaysLeavesTab() {
  const calendarRef = useRef(null);
  const [isNarrow, setIsNarrow] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 1199 : false,
  );
  const [authTick, setAuthTick] = useState(0);
  const [calendarYear, setCalendarYear] = useState(() =>
    new Date().getFullYear(),
  );
  const [apiHolidayEvents, setApiHolidayEvents] = useState([]);
  const [localEvents, setLocalEvents] = useState([]);
  const [holidaysLoading, setHolidaysLoading] = useState(false);

  const events = useMemo(
    () => [...apiHolidayEvents, ...localEvents],
    [apiHolidayEvents, localEvents],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [modalReadOnly, setModalReadOnly] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [title, setTitle] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    const onResize = () => {
      const next = window.innerWidth <= 1199;
      setIsNarrow((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    const bump = () => setAuthTick((n) => n + 1);
    window.addEventListener("erpAuthUpdated", bump);
    window.addEventListener("permissionsUpdated", bump);
    return () => {
      window.removeEventListener("erpAuthUpdated", bump);
      window.removeEventListener("permissionsUpdated", bump);
    };
  }, []);

  const fetchHolidayList = useCallback(async () => {
    const tid = resolveTenantId();
    if (!tid) {
      setApiHolidayEvents([]);
      return;
    }
    setHolidaysLoading(true);
    try {
      const res = await hrmsApi(
        "holiday_list",
        withTenantId({ year: Number(calendarYear) }, tid),
      );
      if (isErpApiSuccess(res) && res.data) {
        const list = Array.isArray(res.data.holidays) ? res.data.holidays : [];
        setApiHolidayEvents(mapHolidayListToFcEvents(list));
      } else {
        setApiHolidayEvents([]);
        if (res?.success === false && res?.message) {
          toast.error(res.message);
        }
      }
    } catch (err) {
      hrmsErrorToast(err, "Could not load holidays.");
      setApiHolidayEvents([]);
    } finally {
      setHolidaysLoading(false);
    }
  }, [calendarYear]);

  useEffect(() => {
    void fetchHolidayList();
  }, [fetchHolidayList, authTick]);

  const handleDatesSet = useCallback((info) => {
    const y = info.view.currentStart.getFullYear();
    setCalendarYear((prev) => (prev !== y ? y : prev));
  }, []);

  /** Keep calendar view in sync with `calendar-init.js` breakpoint behaviour */
  useEffect(() => {
    const api = calendarRef.current?.getApi();
    if (!api) return;
    if (isNarrow) {
      api.changeView("listWeek");
      api.setOption("height", 900);
    } else {
      api.changeView("dayGridMonth");
      api.setOption("height", "auto");
    }
  }, [isNarrow]);

  const calendarsEventsFilter = CALENDAR_KEY_TO_CLASS;

  const resetModalForm = () => {
    setTitle("");
    setStartDate("");
    setEndDate("");
    setEditingEventId(null);
    setModalMode("add");
    setModalReadOnly(false);
  };

  const openAddModalWithRange = useCallback((startStr, endStr) => {
    setModalMode("add");
    setModalReadOnly(false);
    setEditingEventId(null);
    setTitle("");
    const startSlice = String(startStr).slice(0, 10);
    const endExclusive = String(endStr).slice(0, 10);
    const prev = new Date(endExclusive);
    prev.setDate(prev.getDate() - 1);
    const endInclusive = `${prev.getFullYear()}-${paddedMonth(prev)}-${String(prev.getDate()).padStart(2, "0")}`;
    setStartDate(startSlice);
    setEndDate(endInclusive);
    setModalOpen(true);
  }, []);

  const openAddModalToday = useCallback(() => {
    const d = new Date();
    const startSlice = `${d.getFullYear()}-${paddedMonth(d)}-${String(d.getDate()).padStart(2, "0")}`;
    const nextDay = new Date(d);
    nextDay.setDate(nextDay.getDate() + 1);
    const endExclusive = `${nextDay.getFullYear()}-${paddedMonth(nextDay)}-${String(nextDay.getDate()).padStart(2, "0")}`;
    openAddModalWithRange(`${startSlice}T00:00:00`, `${endExclusive}T00:00:00`);
  }, [openAddModalWithRange]);

  const openEditModalFromEvent = useCallback((ev) => {
    setModalMode("edit");
    setModalReadOnly(isHrmsHolidayEventId(ev.id));
    setEditingEventId(ev.id);
    setTitle(ev.title || "");
    const s = ev.startStr ? ev.startStr.slice(0, 10) : "";
    let endInclusive = s;
    if (ev.endStr) {
      const e = new Date(ev.endStr);
      if (ev.allDay) {
        const dayBefore = new Date(e);
        dayBefore.setDate(dayBefore.getDate() - 1);
        endInclusive = `${dayBefore.getFullYear()}-${paddedMonth(dayBefore)}-${String(dayBefore.getDate()).padStart(2, "0")}`;
      } else {
        endInclusive = ev.endStr.slice(0, 10);
      }
    }
    setStartDate(s);
    setEndDate(endInclusive || s);
    setModalOpen(true);
  }, []);

  const handleSelect = useCallback(
    (info) => {
      openAddModalWithRange(info.startStr, info.endStr);
    },
    [openAddModalWithRange],
  );

  const handleEventClick = useCallback(
    (info) => {
      if (info.event.url) {
        info.jsEvent.preventDefault();
        window.open(info.event.url);
        return;
      }
      openEditModalFromEvent(info.event);
    },
    [openEditModalFromEvent],
  );

  const handleEventDidMount = useCallback((info) => {
    const raw = info.event?.extendedProps?.calendar;
    const fcBg = CALENDAR_KEY_TO_CLASS[raw];
    if (!fcBg) return;

    const shell = essResolveEventAnchor(info.el);
    if (!shell) return;

    const paint = () => essPaintUnifiedEventAccent(info.el, fcBg);
    paint();
    queueMicrotask(paint);
    requestAnimationFrame(() => {
      paint();
      requestAnimationFrame(paint);
    });
    window.setTimeout(paint, 0);
    window.setTimeout(paint, 150);

    const mo = new MutationObserver(() => paint());
    mo.observe(shell, {
      attributes: true,
      attributeFilter: ["style", "class"],
    });
    window.setTimeout(() => mo.disconnect(), 800);
  }, []);

  const persistNewEvent = () => {
    if (!title.trim()) return;
    const id = `ess-${Date.now()}`;
    const endInclusive =
      String(endDate || "")
        .trim()
        .slice(0, 10) ||
      String(startDate || "")
        .trim()
        .slice(0, 10);
    const hasRange = endInclusive > String(startDate || "").slice(0, 10);
    setLocalEvents((prev) => [
      ...prev,
      {
        id,
        title: title.trim(),
        start: startDate,
        end: hasRange ? shiftEndExclusive(endInclusive) : undefined,
        allDay: true,
        extendedProps: {
          calendar: "Warning",
          kind: "leave",
          source: "local",
        },
      },
    ]);
    resetModalForm();
    setModalOpen(false);
  };

  const persistUpdateEvent = () => {
    const id = editingEventId;
    if (!id || !title.trim()) return;
    if (isHrmsHolidayEventId(id)) {
      toast.error(
        "Company holidays are managed in HRMS and cannot be edited here.",
      );
      return;
    }
    setLocalEvents((prev) =>
      prev.map((e) =>
        String(e.id) === String(id)
          ? {
              ...e,
              title: title.trim(),
              start: startDate,
              end:
                endDate && endDate !== startDate
                  ? shiftEndExclusive(endDate)
                  : undefined,
              allDay: true,
              extendedProps: {
                ...(e.extendedProps || {}),
              },
            }
          : e,
      ),
    );
    resetModalForm();
    setModalOpen(false);
  };

  const customButtons = useMemo(
    () => ({
      requestLeaveButton: {
        text: "Request leave",
        click: () => openAddModalToday(),
      },
    }),
    [openAddModalToday],
  );

  /** FullCalendar expects exclusive end date for all-day spans */
  function shiftEndExclusive(endInclusiveStr) {
    const d = new Date(`${endInclusiveStr}T00:00:00`);
    d.setDate(d.getDate() + 1);
    return `${d.getFullYear()}-${paddedMonth(d)}-${String(d.getDate()).padStart(2, "0")}`;
  }

  return (
    <div className="row g-3">
      <div className="col-12">
        <div className="app-calendar ess-holidays-leaves-calendar border rounded overflow-hidden bg-white calendar-container">
          <div className="ess-calendar-wrap p-2">
            <div className="ess-holidays-legend-bar d-flex flex-wrap align-items-center justify-content-end gap-2 gap-md-3">
              {holidaysLoading ? (
                <Spinner
                  animation="border"
                  size="sm"
                  className="text-secondary"
                />
              ) : null}
              {ESS_LEGEND_ITEMS.map(({ key, label, background }) => (
                <span
                  key={key}
                  className="ess-holidays-legend-pill d-inline-flex align-items-center gap-1 text-muted"
                  title={label}
                >
                  <span
                    className="rounded-circle flex-shrink-0 ess-holidays-legend-dot"
                    style={{ background }}
                  />
                  <span className="ess-holidays-legend-label text-nowrap">
                    {label}
                  </span>
                </span>
              ))}
            </div>
            <FullCalendar
              ref={calendarRef}
              plugins={[
                dayGridPlugin,
                timeGridPlugin,
                listPlugin,
                interactionPlugin,
              ]}
              selectable
              editable={false}
              initialView={isNarrow ? "listWeek" : "dayGridMonth"}
              height={isNarrow ? 900 : "auto"}
              headerToolbar={{
                left: "prev next requestLeaveButton",
                center: "title",
                right: "dayGridMonth,timeGridWeek,timeGridDay",
              }}
              customButtons={customButtons}
              events={events}
              datesSet={handleDatesSet}
              select={handleSelect}
              eventClick={handleEventClick}
              eventDidMount={handleEventDidMount}
              eventClassNames={({ event }) => {
                const key = event.extendedProps?.calendar;
                const val = calendarsEventsFilter[key];
                return val ? ["event-fc-color", `fc-bg-${val}`] : [];
              }}
            />
          </div>
        </div>
      </div>

      <Modal
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          resetModalForm();
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="h6 mb-0">
            {modalMode === "edit" ? "Leave / holiday details" : "Request leave"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="small">Title</Form.Label>
            <Form.Control
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Annual leave"
              disabled={modalReadOnly}
            />
          </Form.Group>
          <div className="row g-2">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="small">Start</Form.Label>
                <Form.Control
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  disabled={modalReadOnly}
                />
              </Form.Group>
            </div>
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label className="small">End (inclusive)</Form.Label>
                <Form.Control
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  disabled={modalReadOnly}
                />
              </Form.Group>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top gap-2">
          {modalMode === "edit" ? (
            <Button
              type="button"
              variant="primary"
              disabled={modalReadOnly || !title.trim()}
              onClick={() => persistUpdateEvent()}
              className="btn-update-event"
            >
              Update
            </Button>
          ) : (
            <Button
              type="button"
              variant="primary"
              disabled={!title.trim() || !startDate}
              onClick={() => persistNewEvent()}
            >
              Save
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
}
